import React, { useState, useEffect, useCallback, useRef } from "react";
import { PLANNING_NOTIFICATION_EVENT } from "../utils/planningNotificationStream";
import {
  Card,
  List,
  Button,
  Typography,
  Spin,
  Empty,
  Tag,
  Avatar,
  Space,
  Divider,
  Tooltip,
  message,
  Modal,
  Descriptions,
  Select,
  Input,
  Row,
  Col,
  Statistic,
  theme,
} from "antd";
import {
  BellOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  FireOutlined,
  SwapOutlined,
  FilterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import apiClient from "../api/apiClient";
import { createScopedRequestGate, isScopedRequestCanceled } from "../utils/scopedRequestGate";
import {
  getNotificationAction,
  getNotificationDetailRows,
} from "../utils/notificationDetailDisplay";
import DailyRecapDetail, {
  DailyRecapModalTitle,
} from "../components/DailyRecapDetail";
import { useNavigate } from "react-router-dom";
import "../styles/notification-details-modal.css";

const { Text, Title } = Typography;
const { Option } = Select;

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  priority: "low" | "medium" | "high" | "critical";
  created_at: string;
  is_read: boolean;
  is_dismissed: boolean;
  data?: any;
  warehouse_id?: number;
  warehouse_name?: string;
}

const NotificationsList: React.FC = () => {
  const { token: themeToken } = theme.useToken();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filterType, setFilterType] = useState<string | undefined>(undefined);
  const [filterPriority, setFilterPriority] = useState<string | undefined>(undefined);
  const [filterRead, setFilterRead] = useState<boolean | undefined>(undefined);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [confirming, setConfirming] = useState(false);

  const authContext = useAuth() as { user: any } | null;
  const user = authContext?.user;
  const navigate = useNavigate();
  const {
    selectedCompany: selectedCompanyId,
    selectedWarehouse: selectedWarehouseId,
  } = useSelection();
  const scopeKey = `${user?.id}:${selectedCompanyId}:${selectedWarehouseId}`;
  const currentScope = useRef(scopeKey); currentScope.current = scopeKey;
  const requestGate = useRef(createScopedRequestGate());

  // Fetch notifications
  const fetchNotifications = useCallback(async (showLoading = true) => {
    if (!user?.id || !selectedCompanyId) {
      return;
    }

    const request = requestGate.current.start(scopeKey);
    try {
      if (showLoading) setLoading(true);

      const params: any = {
        company_id: selectedCompanyId,
        user_id: user.id,
        warehouse_id: selectedWarehouseId || undefined,
        page: currentPage,
        limit: pageSize,
        sort_by: "created_at",
        sort_order: "DESC",
      };

      if (filterType) params.type = filterType;
      if (filterPriority) params.priority = filterPriority;
      if (filterRead !== undefined) params.is_read = filterRead;

      const response = await apiClient.get("/api/notifications", { params, signal: request.signal, skipGlobalLoading: !showLoading });

      if (!request.isCurrent(currentScope.current)) return;
      setNotifications(response.data.notifications || []);
      setTotal(response.data.pagination?.totalItems ?? response.data.total ?? 0);
    } catch (error: any) {
      if (isScopedRequestCanceled(error, request.signal) || !request.isCurrent(currentScope.current)) return;
      console.error("Error fetching notifications:", error);
      message.error("Erreur lors du chargement des notifications");
    } finally {
      const current = request.isCurrent(currentScope.current); request.finish();
      if (current) setLoading(false);
    }
  }, [scopeKey, user?.id, selectedCompanyId, selectedWarehouseId, currentPage, pageSize, filterType, filterPriority, filterRead]);

  // Mark as read
  const markAsRead = async (notificationId: number) => {
    try {
      await apiClient.post(`/api/notifications/mark-read/${notificationId}`, {
        user_id: user?.id,
        company_id: selectedCompanyId,
      });

      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      message.success("Marqué comme lu");
    } catch (error) {
      console.error("Error marking as read:", error);
      message.error("Erreur lors du marquage");
    }
  };

  // Mark as unread
  const markAsUnread = async (notificationId: number) => {
    try {
      await apiClient.post(`/api/notifications/mark-unread/${notificationId}`, {
        user_id: user?.id,
        company_id: selectedCompanyId,
      });

      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: false } : n))
      );
      message.success("Marqué comme non lu");
    } catch (error) {
      console.error("Error marking as unread:", error);
      message.error("Erreur lors du marquage");
    }
  };

  // Dismiss notification
  const dismissNotification = async (notificationId: number) => {
    try {
      await apiClient.post(`/api/notifications/dismiss/${notificationId}`, {
        user_id: user?.id,
        company_id: selectedCompanyId,
      });

      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      message.success("Notification écartée");
    } catch (error) {
      console.error("Error dismissing notification:", error);
      message.error("Erreur lors de l'écartement");
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await apiClient.post("/api/notifications/mark-all-read", {
        user_id: user?.id,
        company_id: selectedCompanyId,
        warehouse_id: selectedWarehouseId || undefined,
      });

      fetchNotifications();
      message.success("Toutes les notifications ont été marquées comme lues");
    } catch (error) {
      console.error("Error marking all as read:", error);
      message.error("Erreur lors du marquage");
    }
  };

  // Get priority display
  const getPriorityDisplay = (priority: string) => {
    switch (priority) {
      case "critical":
        return { icon: <FireOutlined />, color: "#ff4d4f", label: "Critique" };
      case "high":
        return {
          icon: <ExclamationCircleOutlined />,
          color: "#fa8c16",
          label: "Élevée",
        };
      case "medium":
        return {
          icon: <WarningOutlined />,
          color: "#fadb14",
          label: "Moyenne",
        };
      case "low":
        return {
          icon: <InfoCircleOutlined />,
          color: "#52c41a",
          label: "Basse",
        };
      default:
        return {
          icon: <InfoCircleOutlined />,
          color: "#1890ff",
          label: "Normale",
        };
    }
  };

  // Get notification type display
  const getTypeDisplay = (type: string) => {
    const typeMap: Record<string, { label: string; color: string }> = {
      stock_alert: { label: "Stock", color: "red" },
      payment_due: { label: "Paiement", color: "orange" },
      subscription_billing_alert: {
        label: "Souscription",
        color: "volcano",
      },
      order_pending: { label: "Commande", color: "blue" },
      production_alert: { label: "Production", color: "purple" },
      expense_alert: { label: "Dépense", color: "gold" },
      expense_supplier_missing: {
        label: "Fournisseur manquant",
        color: "orange",
      },
      expense_due_alert: {
        label: "Échéance aujourd’hui",
        color: "red",
      },
      payables_regularization_alert: {
        label: "À régulariser",
        color: "gold",
      },
      sales_receivables_alert: {
        label: "Factures à encaisser",
        color: "volcano",
      },
      user_action: { label: "Action", color: "green" },
      system_maintenance: { label: "Système", color: "gray" },
      report_ready: { label: "Rapport", color: "cyan" },
      transfer_received: { label: "Transfert Reçu", color: "green" },
      transfer_confirmed: { label: "Transfert Confirmé", color: "blue" },
      restaurant_new_order: { label: "Commande Restaurant", color: "cyan" },
      sale_completed: { label: "Vente", color: "green" },
      purchase_received: { label: "Achat", color: "geekblue" },
      stock_adjustment: { label: "Ajustement Stock", color: "orange" },
      stock_supply_practice_alert: {
        label: "Approvisionnement à vérifier",
        color: "gold",
      },
      production_completed: { label: "Production", color: "purple" },
      daily_recap: { label: "Bilan du jour", color: "gold" },
      planning_event: { label: "Planification équipe", color: "blue" },
    };

    return typeMap[type] || { label: type, color: "default" };
  };

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "À l'instant";
    if (diffInMinutes < 60) return `Il y a ${diffInMinutes}min`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Il y a ${diffInHours}h`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `Il y a ${diffInDays}j`;

    return date.toLocaleDateString("fr-FR");
  };

  // Open notification details
  const handleOpenDetails = (notification: Notification) => {
    setSelectedNotification(notification);
    setDetailsModalVisible(true);
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
  };

  // Confirm transfer reception
  const handleConfirmReception = async () => {
    if (!selectedNotification?.data) return;

    try {
      setConfirming(true);

      // Parse notification data to get order_id
      const data =
        typeof selectedNotification.data === "string"
          ? JSON.parse(selectedNotification.data)
          : selectedNotification.data;

      const orderId = data.order_id;

      if (!orderId) {
        message.error("ID de commande introuvable");
        return;
      }

      // Call confirmation API
      await apiClient.post(`/api/orders/${orderId}/confirm-reception`);

      message.success("Réception confirmée avec succès !");

      // Close modal and refresh notifications
      setDetailsModalVisible(false);
      dismissNotification(selectedNotification.id); // Remove from list
      fetchNotifications();
    } catch (error: any) {
      console.error("Failed to confirm reception:", error);
      message.error(
        error.response?.data?.error || "Erreur lors de la confirmation"
      );
    } finally {
      setConfirming(false);
    }
  };

  // Render detailed content
  const renderDetailedContent = () => {
    if (!selectedNotification) return null;

    const priorityDisplay = getPriorityDisplay(selectedNotification.priority);
    const typeDisplay = getTypeDisplay(selectedNotification.type);

    let parsedData = selectedNotification.data;
    if (typeof parsedData === "string") {
      try {
        parsedData = JSON.parse(parsedData);
      } catch (e) {
        console.error("Error parsing notification data:", e);
      }
    }
    const genericDetailRows = parsedData
      ? getNotificationDetailRows(selectedNotification.type, parsedData)
      : [];

    if (selectedNotification.type === "daily_recap") {
      return (
        <DailyRecapDetail
          data={parsedData && typeof parsedData === "object" ? parsedData : {}}
          message={selectedNotification.message}
          createdAt={selectedNotification.created_at}
          warehouseNameFallback={selectedNotification.warehouse_name}
        />
      );
    }

    return (
      <div>
        <Space style={{ marginBottom: 16 }}>
          <Tag color={typeDisplay.color}>{typeDisplay.label}</Tag>
          <Tag color={priorityDisplay.color}>Priorité : {priorityDisplay.label}</Tag>
          {selectedNotification.warehouse_name && (
            <Tag icon={<SwapOutlined />}>{selectedNotification.warehouse_name}</Tag>
          )}
        </Space>

        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="Résumé">
            <span style={{ whiteSpace: "pre-line" }}>
              {selectedNotification.message}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Reçue le">
            {new Date(selectedNotification.created_at).toLocaleString("fr-FR", {
              dateStyle: "full",
              timeStyle: "short",
            })}
          </Descriptions.Item>
        </Descriptions>

        {selectedNotification.type === "transfer_received" && parsedData && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Détails du Transfert</Title>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Numéro de Transfert" span={2}>
                {parsedData.transfer_number}
              </Descriptions.Item>
              <Descriptions.Item label="Établissement Source">
                {parsedData.from_warehouse_name}
              </Descriptions.Item>
              <Descriptions.Item label="Établissement Destination">
                {parsedData.to_warehouse_name}
              </Descriptions.Item>
              <Descriptions.Item label="Nombre de Produits">
                {parsedData.total_items}
              </Descriptions.Item>
              <Descriptions.Item label="Quantité Totale">
                {parsedData.total_quantity} unité(s)
              </Descriptions.Item>
              <Descriptions.Item label="Montant Total" span={2}>
                {parsedData.total_amount?.toLocaleString("fr-FR")} FCFA
              </Descriptions.Item>
            </Descriptions>

            {parsedData.items && parsedData.items.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>Produits Transférés</Title>
                <List
                  size="small"
                  bordered
                  dataSource={parsedData.items}
                  renderItem={(item: any) => (
                    <List.Item>
                      <div style={{ width: "100%" }}>
                        <Text strong>{item.product_name}</Text>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                          <Text type="secondary">
                            Quantité: {item.quantity}
                          </Text>
                          <Text type="secondary">
                            Prix: {item.unit_price?.toLocaleString("fr-FR")} FCFA
                          </Text>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        )}

        {selectedNotification.type === "transfer_confirmed" && parsedData && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Détails de la Confirmation</Title>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Numéro de Transfert" span={2}>
                {parsedData.transfer_number}
              </Descriptions.Item>
              <Descriptions.Item label="Établissement Source (Vous)">
                {parsedData.from_warehouse_name}
              </Descriptions.Item>
              <Descriptions.Item label="Établissement Destinataire">
                {parsedData.to_warehouse_name}
              </Descriptions.Item>
              <Descriptions.Item label="Nombre de Produits">
                {parsedData.total_items}
              </Descriptions.Item>
              <Descriptions.Item label="Quantité Totale">
                {parsedData.total_quantity} unité(s)
              </Descriptions.Item>
              <Descriptions.Item label="Montant Total" span={2}>
                {parsedData.total_amount?.toLocaleString("fr-FR")} FCFA
              </Descriptions.Item>
            </Descriptions>

            {parsedData.items && parsedData.items.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>Produits du Transfert</Title>
                <List
                  size="small"
                  bordered
                  dataSource={parsedData.items}
                  renderItem={(item: any) => (
                    <List.Item>
                      <div style={{ width: "100%" }}>
                        <Text strong>{item.product_name}</Text>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                          <Text type="secondary">
                            Quantité: {item.quantity}
                          </Text>
                          <Text type="secondary">
                            Prix: {item.unit_price?.toLocaleString("fr-FR")} FCFA
                          </Text>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        )}

        {selectedNotification.type !== "transfer_received" &&
         selectedNotification.type !== "transfer_confirmed" &&
         selectedNotification.type !== "daily_recap" &&
         genericDetailRows.length > 0 &&
         parsedData && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Détails utiles</Title>
            <Descriptions bordered column={1} size="small">
              {genericDetailRows.map((row) => (
                <Descriptions.Item key={row.key} label={row.label}>
                  {row.value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </div>
        )}
      </div>
    );
  };

  const selectedNotificationData = (() => {
    if (!selectedNotification?.data) return null;
    try {
      return (
        typeof selectedNotification.data === "string"
          ? JSON.parse(selectedNotification.data)
          : selectedNotification.data
      );
    } catch (_) {
      return null;
    }
  })();
  const selectedNotificationAction =
    selectedNotification && selectedNotificationData
      ? getNotificationAction(
          selectedNotification.type,
          selectedNotificationData,
          selectedNotification.warehouse_id
        )
      : null;
  const isSelectedDailyRecap = selectedNotification?.type === "daily_recap";

  // Initial load
  useEffect(() => {
    setNotifications([]); setSelectedNotification(null); setDetailsModalVisible(false); setTotal(0);
  }, [scopeKey]);

  useEffect(() => {
    fetchNotifications();
    const refresh = () => { void fetchNotifications(false); };
    window.addEventListener(PLANNING_NOTIFICATION_EVENT, refresh);
    return () => { requestGate.current.cancel(); window.removeEventListener(PLANNING_NOTIFICATION_EVENT, refresh); };
  }, [fetchNotifications]);

  // Calculate stats
  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const criticalCount = notifications.filter((n) => n.priority === "critical").length;

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <Title level={2}>
          <BellOutlined /> Toutes les Notifications
        </Title>

        {/* Statistics */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total"
                value={total}
                prefix={<BellOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Non Lues"
                value={unreadCount}
                valueStyle={{ color: "#1890ff" }}
                prefix={<EyeOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Critiques"
                value={criticalCount}
                valueStyle={{ color: "#ff4d4f" }}
                prefix={<FireOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            placeholder="Type de notification"
            allowClear
            style={{ width: 200 }}
            value={filterType}
            onChange={setFilterType}
          >
            <Option value="stock_alert">Stock</Option>
            <Option value="payment_due">Paiement</Option>
            <Option value="subscription_billing_alert">Souscription</Option>
            <Option value="order_pending">Commande</Option>
            <Option value="restaurant_new_order">Commande restaurant</Option>
            <Option value="transfer_received">Transfert</Option>
            <Option value="production_alert">Production</Option>
            <Option value="expense_alert">Dépense</Option>
            <Option value="expense_supplier_missing">
              Fournisseur de dépense manquant
            </Option>
            <Option value="expense_due_alert">
              Dépenses à échéance aujourd’hui
            </Option>
            <Option value="payables_regularization_alert">
              Dépenses et achats à régulariser
            </Option>
            <Option value="sales_receivables_alert">
              Factures de vente à encaisser
            </Option>
            <Option value="sale_completed">Vente</Option>
            <Option value="purchase_received">Achat</Option>
            <Option value="stock_adjustment">Ajustement stock</Option>
            <Option value="stock_supply_practice_alert">
              Approvisionnement à vérifier
            </Option>
            <Option value="production_completed">Production terminée</Option>
            <Option value="daily_recap">Bilan du jour</Option>
            <Option value="planning_event">Planification équipe</Option>
          </Select>

          <Select
            placeholder="Priorité"
            allowClear
            style={{ width: 150 }}
            value={filterPriority}
            onChange={setFilterPriority}
          >
            <Option value="critical">Critique</Option>
            <Option value="high">Élevée</Option>
            <Option value="medium">Moyenne</Option>
            <Option value="low">Basse</Option>
          </Select>

          <Select
            placeholder="Statut de lecture"
            allowClear
            style={{ width: 150 }}
            value={filterRead}
            onChange={setFilterRead}
          >
            <Option value={false}>Non lues</Option>
            <Option value={true}>Lues</Option>
          </Select>

          <Button icon={<ReloadOutlined />} onClick={() => fetchNotifications()}>
            Actualiser
          </Button>

          <Button onClick={markAllAsRead} disabled={unreadCount === 0}>
            Tout marquer comme lu
          </Button>
        </Space>

        <Divider />

        {/* Notifications List */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : notifications.length === 0 ? (
          <Empty description="Aucune notification" />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={notifications}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size || 20);
              },
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} notifications`,
            }}
            renderItem={(item) => {
              const priorityDisplay = getPriorityDisplay(item.priority);
              const typeDisplay = getTypeDisplay(item.type);

              return (
                <List.Item
                  className={`notification-item ${!item.is_read ? "unread" : ""}`}
                  style={{
                    cursor: "pointer",
                    backgroundColor: !item.is_read ? themeToken.colorInfoBg : themeToken.colorBgContainer,
                    padding: "16px",
                    marginBottom: "8px",
                    borderRadius: "4px",
                    border: `1px solid ${themeToken.colorBorderSecondary}`,
                  }}
                  onClick={() => handleOpenDetails(item)}
                  actions={[
                    <Tooltip
                      title={
                        item.is_read
                          ? "Marquer comme non lu"
                          : "Marquer comme lu"
                      }
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={
                          item.is_read ? <EyeOutlined /> : <CheckOutlined />
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          item.is_read
                            ? markAsUnread(item.id)
                            : markAsRead(item.id);
                        }}
                      />
                    </Tooltip>,
                    <Tooltip title="Écarter">
                      <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          dismissNotification(item.id);
                        }}
                        danger
                      />
                    </Tooltip>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={priorityDisplay.icon}
                        style={{ backgroundColor: priorityDisplay.color }}
                      />
                    }
                    title={
                      <div>
                        <Text strong={!item.is_read}>{item.title}</Text>
                        <Space size={4} style={{ marginLeft: 8 }}>
                          <Tag color={typeDisplay.color}>
                            {typeDisplay.label}
                          </Tag>
                          {item.warehouse_name && (
                            <Tag>{item.warehouse_name}</Tag>
                          )}
                        </Space>
                      </div>
                    }
                    description={
                      <div>
                        <Text>{item.message}</Text>
                        <div style={{ marginTop: 4 }}>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            {formatRelativeTime(item.created_at)}
                          </Text>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Card>

      {/* Notification Details Modal */}
      <Modal
        className={`notification-details-modal${
          isSelectedDailyRecap ? " notification-details-modal--daily-recap" : ""
        }`}
        centered
        title={
          isSelectedDailyRecap ? (
            <DailyRecapModalTitle
              data={selectedNotificationData}
              createdAt={selectedNotification?.created_at}
              warehouseNameFallback={selectedNotification?.warehouse_name}
            />
          ) : (
            <div className="notification-modal-title">
              <Avatar
                icon={
                  selectedNotification
                    ? getPriorityDisplay(selectedNotification.priority).icon
                    : <InfoCircleOutlined />
                }
                style={{
                  backgroundColor: selectedNotification
                    ? getPriorityDisplay(selectedNotification.priority).color
                    : "#1890ff",
                }}
                size={36}
              />
              <div className="notification-modal-title__text">
                {selectedNotification?.title || "Détails de la notification"}
              </div>
            </div>
          )
        }
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={
          isSelectedDailyRecap
            ? [
                <Button key="close" onClick={() => setDetailsModalVisible(false)}>
                  Fermer
                </Button>,
                selectedNotificationAction && (
                  <Button
                    key="open-related"
                    type="primary"
                    onClick={() => {
                      setDetailsModalVisible(false);
                      navigate(selectedNotificationAction.url);
                    }}
                  >
                    {selectedNotificationAction.label}
                  </Button>
                ),
              ]
            : [
                selectedNotification?.type === "transfer_received" && (
                  <Button
                    key="confirm"
                    type="primary"
                    onClick={handleConfirmReception}
                    loading={confirming}
                    icon={<CheckOutlined />}
                  >
                    Confirmer réception
                  </Button>
                ),
                selectedNotificationAction && (
                  <Button
                    key="open-related"
                    type="primary"
                    onClick={() => {
                      setDetailsModalVisible(false);
                      navigate(selectedNotificationAction.url);
                    }}
                  >
                    {selectedNotificationAction.label}
                  </Button>
                ),
                <Button key="close" onClick={() => setDetailsModalVisible(false)}>
                  Fermer
                </Button>,
              ]
        }
        width={isSelectedDailyRecap ? 960 : 760}
      >
        {renderDetailedContent()}
      </Modal>
    </div>
  );
};

export default NotificationsList;
